import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useLang } from "../../contexts/LanguageContext";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, RotateCcw } from "lucide-react";

export default function AdminSettings() {
  const { user, isSuperAdmin } = useAuth();
  const { lang } = useLang();
  const [resetOpen, setResetOpen] = useState(false);
  const [resetMode, setResetMode] = useState("demo"); // "demo" | "prod"
  const [confirmText, setConfirmText] = useState("");
  const [keepEmails, setKeepEmails] = useState("");
  const [resetting, setResetting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const openDialog = (mode) => {
    setResetMode(mode);
    setConfirmText("");
    setResetOpen(true);
  };

  const runReset = async () => {
    if (confirmText !== "RESET") {
      toast.error(lang === "fr" ? "Tape RESET en majuscules pour confirmer" : "Type RESET in capitals to confirm");
      return;
    }
    setResetting(true);
    try {
      const keep = keepEmails.split(/[,\s]+/).map(s => s.trim()).filter(Boolean);
      const isProd = resetMode === "prod";
      const r = await api.post("/admin/data/reset-fakes", {
        confirm: "RESET",
        keep_emails: keep,
        reseed_fakes: !isProd,
        keep_permanent: !isProd,
      });
      setLastResult(r.data);
      toast.success(lang === "fr"
        ? (isProd
            ? `PROD nettoyée · ${r.data.deleted_users + r.data.deleted_families} comptes/familles supprimés, ${r.data.deleted_students} élèves supprimés`
            : `${r.data.seeded_students} élèves créés · ${r.data.deleted_users + r.data.deleted_families} comptes/familles supprimés`)
        : (isProd
            ? `PROD wiped · ${r.data.deleted_users + r.data.deleted_families} accounts/families deleted, ${r.data.deleted_students} students deleted`
            : `${r.data.seeded_students} students created · ${r.data.deleted_users + r.data.deleted_families} accounts/families deleted`));
      setResetOpen(false);
      setConfirmText("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setResetting(false);
    }
  };

  return (
    <div data-testid="admin-settings" className="space-y-6">
      <div>
        <p className="overline mb-1">OFA France · Admin</p>
        <h1 className="font-serif text-3xl tracking-tight">
          {lang === "fr" ? "Paramètres" : "Settings"}
        </h1>
      </div>

      <Card>
        <CardHeader><CardTitle>{lang === "fr" ? "Mon compte" : "Account"}</CardTitle></CardHeader>
        <CardContent className="space-y-1 text-sm">
          <div className="flex justify-between border-b border-border/50 py-1.5">
            <span className="text-muted-foreground">Email</span><span>{user?.email}</span>
          </div>
          <div className="flex justify-between border-b border-border/50 py-1.5">
            <span className="text-muted-foreground">{lang === "fr" ? "Rôle" : "Role"}</span>
            <span className="font-mono text-xs">{user?.role}</span>
          </div>
          <div className="flex justify-between border-b border-border/50 py-1.5">
            <span className="text-muted-foreground">{lang === "fr" ? "Nom" : "Name"}</span>
            <span>{user?.first_name} {user?.last_name}</span>
          </div>
        </CardContent>
      </Card>

      {isSuperAdmin && (
        <Card className="border-destructive/30 bg-destructive/[0.02]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-4 h-4" />
              {lang === "fr" ? "Zone dangereuse · Réinitialisation des données" : "Danger zone · Data reset"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground leading-relaxed">
              {lang === "fr"
                ? "Cette action supprime TOUS les comptes parents, familles, élèves, candidatures, inscriptions, paiements et messages, puis recrée un jeu de données de démo propre (Alessandro Fontana + 20 élèves répartis sur élémentaire/collège/lycée). Les comptes équipe (admin, professeur, super-admin) sont préservés."
                : "This action deletes ALL parent accounts, families, students, applications, enrollments, payments and messages, then recreates a clean demo dataset (Alessandro Fontana + 20 students across elementary/middle/high school). Staff accounts (admin, teacher, super-admin) are preserved."}
            </p>
            <p className="text-xs text-muted-foreground">
              {lang === "fr"
                ? "💡 Tu peux indiquer ci-dessous des emails parents à préserver (ex. de vraies familles inscrites)."
                : "💡 You can list parent emails to preserve below (e.g. real enrolled families)."}
            </p>

            {lastResult && (
              <div className="text-xs bg-secondary/40 rounded p-3 space-y-0.5 font-mono">
                <div className="font-semibold text-foreground mb-1">{lang === "fr" ? "Dernier résultat :" : "Last result:"}</div>
                <div>· {lang === "fr" ? "Comptes supprimés" : "Accounts deleted"}: {lastResult.deleted_users}</div>
                <div>· {lang === "fr" ? "Familles supprimées" : "Families deleted"}: {lastResult.deleted_families}</div>
                <div>· {lang === "fr" ? "Élèves supprimés" : "Students deleted"}: {lastResult.deleted_students}</div>
                <div>· {lang === "fr" ? "Candidatures supprimées" : "Applications deleted"}: {lastResult.deleted_applications}</div>
                <div>· {lang === "fr" ? "Paiements supprimés" : "Payments deleted"}: {lastResult.deleted_payments}</div>
                <div className="text-emerald-700 mt-1">
                  · {lang === "fr" ? "Élèves recréés" : "Students recreated"}: {lastResult.seeded_students}
                </div>
              </div>
            )}

            <Button
              variant="destructive"
              onClick={() => openDialog("demo")}
              data-testid="open-reset-fakes-btn"
            >
              <RotateCcw className="w-4 h-4 mr-1.5" />
              {lang === "fr" ? "Réinitialiser les données de démo" : "Reset demo data"}
            </Button>

            <div className="pt-3 mt-3 border-t border-destructive/30">
              <p className="text-sm font-semibold text-destructive mb-1">
                {lang === "fr" ? "🧨 Mode PRODUCTION — Effacer toute la démo (0 fake)" : "🧨 PRODUCTION mode — Wipe all demo (0 fakes)"}
              </p>
              <p className="text-xs text-muted-foreground leading-relaxed mb-2">
                {lang === "fr"
                  ? "Supprime TOUTES les familles/élèves/candidatures/inscriptions/cours fictifs ET les comptes démo permanents (Alessandro, Matteo, Léa). Aucun fake n'est recréé, même après redéploiement. Les 11 réinscriptions et les comptes équipe sont préservés."
                  : "Wipes ALL fake families/students/applications/enrollments/courses AND the permanent demo accounts (Alessandro, Matteo, Léa). No fakes recreated, even after redeploy. The 11 reenrollments and staff accounts are preserved."}
              </p>
              <Button
                variant="destructive"
                onClick={() => openDialog("prod")}
                data-testid="open-prod-wipe-btn"
                className="bg-[#14274B] hover:bg-[#14274B]/90 text-white"
              >
                <AlertTriangle className="w-4 h-4 mr-1.5" />
                {lang === "fr" ? "Vider la production maintenant" : "Wipe production now"}
              </Button>
            </div>

            {/* Quick test fixture — generate a paid test parent without Stripe */}
            <div className="pt-3 mt-3 border-t border-border">
              <p className="text-sm font-semibold mb-1">
                {lang === "fr" ? "🧪 Compte parent de test (sans Stripe)" : "🧪 Test parent account (no Stripe)"}
              </p>
              <p className="text-xs text-muted-foreground leading-relaxed mb-2">
                {lang === "fr"
                  ? "Crée un compte parent + 1 enfant déjà inscrit (frais + acompte marqués payés). Idéal pour tester le questionnaire de disponibilités côté parent sans repasser par Stripe. Idempotent — re-cliquer réinitialise."
                  : "Creates a parent account + 1 fully-paid enrolled student (no Stripe). Ideal for testing the availability questionnaire as a parent. Idempotent — reclick resets."}
              </p>
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const r = await api.post("/admin/data/generate-test-parent", { grade: "CE2", track: "fluent" });
                    const c = r.data.credentials;
                    setLastResult({ ...r.data, testCreds: c });
                    toast.success(lang === "fr"
                      ? `Parent test créé : ${c.email} / ${c.password}`
                      : `Test parent created: ${c.email} / ${c.password}`,
                      { duration: 12000 });
                  } catch (e) {
                    toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
                  }
                }}
                data-testid="generate-test-parent-btn"
              >
                {lang === "fr" ? "Générer un parent de test" : "Generate test parent"}
              </Button>
              {lastResult?.testCreds && (
                <div className="text-xs bg-secondary/40 rounded p-3 mt-2 font-mono">
                  <div>📧 {lastResult.testCreds.email}</div>
                  <div>🔑 {lastResult.testCreds.password}</div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              {resetMode === "prod"
                ? (lang === "fr" ? "Confirmer le wipe PRODUCTION" : "Confirm PRODUCTION wipe")
                : (lang === "fr" ? "Confirmer la réinitialisation" : "Confirm reset")}
            </DialogTitle>
            <DialogDescription>
              {resetMode === "prod"
                ? (lang === "fr"
                    ? "Action irréversible. Toutes les données démo (y compris Alessandro, Matteo, Léa et les 24 cours du catalogue/seed) seront supprimées. Aucune donnée fictive ne sera recréée même après redéploiement. Les 11 réinscriptions et les comptes équipe restent intacts."
                    : "Irreversible action. All demo data (including Alessandro, Matteo, Léa and the 24 catalog/seed courses) will be deleted. No fake data will be recreated even after redeploy. The 11 reenrollments and staff accounts remain intact.")
                : (lang === "fr"
                    ? "Action irréversible. Toutes les données parents seront supprimées et remplacées par un jeu de démo de 20 élèves (Alessandro + 19 autres)."
                    : "Irreversible action. All parent data will be deleted and replaced with a 20-student demo set (Alessandro + 19 others).")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">
                {lang === "fr"
                  ? "Emails parents à préserver (optionnel, séparés par virgule ou espace)"
                  : "Parent emails to keep (optional, comma- or space-separated)"}
              </Label>
              <Input
                data-testid="reset-keep-emails"
                placeholder="famille1@example.com famille2@example.com"
                value={keepEmails}
                onChange={(e) => setKeepEmails(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs text-destructive">
                {lang === "fr"
                  ? "Tape RESET en MAJUSCULES pour confirmer"
                  : "Type RESET in CAPITALS to confirm"}
              </Label>
              <Input
                data-testid="reset-confirm-input"
                autoFocus
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="RESET"
                className="font-mono"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setResetOpen(false)}>
                {lang === "fr" ? "Annuler" : "Cancel"}
              </Button>
              <Button
                variant="destructive"
                onClick={runReset}
                disabled={confirmText !== "RESET" || resetting}
                data-testid="reset-confirm-btn"
              >
                {resetting
                  ? (lang === "fr" ? "Réinitialisation…" : "Resetting…")
                  : (lang === "fr" ? "Réinitialiser maintenant" : "Reset now")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
